(function () {
  "use strict";

  // ------------------------------------------------------------------------------------------
  // SC Cross-sell add-on (DEV-3978)
  //
  // The add-on comes from custom.sc_crosssell_variants. One referenced variant means a fixed
  // offer; several mean the customer picks one from a native select, and this script swaps the
  // ready-formatted price strings the snippet emitted alongside it.
  //
  // ARCHITECTURE: we do NOT intercept the product form's submit. The theme adds the main product
  // exactly as before - with all its properties, its loading state and its error handling - and
  // we append the add-on afterwards, on variant:added.
  //
  // Two reasons this is deliberate:
  //   1. Focal builds its payload from FormData(this), which includes fields apps inject at
  //      runtime in bracket and array notation. Rebuilding that payload by hand breaks it sooner
  //      or later.
  //   2. The Cart API aborts the whole call when one line of a multi-item add fails. Bundling
  //      both products into one request would put the main purchase at risk for the sake of an
  //      optional extra.
  //
  // Loaded on every page (layout/theme.liquid), so everything is delegated on document and stays
  // inert where the markup is absent. Nothing is cached at load time either: the theme re-renders
  // parts of the product page on variant:change and product:rerender, so every handler reads its
  // elements fresh from the DOM.
  // ------------------------------------------------------------------------------------------

  var SLOT_SELECTOR = "[data-sc-crosssell]";
  var INPUT_SELECTOR = "[data-sc-crosssell-input]";
  var SELECT_SELECTOR = "[data-sc-crosssell-select]";
  var PRICES_SELECTOR = "[data-sc-crosssell-prices]";

  // Must outlast CartDrawer's own re-render, which runs 600 ms after variant:added
  // (nextReplacementDelay in theme.js). Without this the drawer would paint our cart first and
  // the theme's older, add-on-less HTML would overwrite it a moment later. Re-check this value
  // when the theme is updated.
  var REPLACEMENT_DELAY = 700;

  // One entry per product form while its add-on request is running, keyed by form id.
  var inFlight = {};

  function escapeAttributeValue(value) {
    return value.replace(/["\\]/g, "\\$&");
  }

  // form.id would return the <input name="id"> element, not the string: HTMLFormElement's named
  // getter overrides built-in properties, and the product form contains exactly such an input.
  // Focal relies on that itself (ProductForm.connectedCallback does this.id.disabled = false).
  // As an object key every form would collapse to "[object HTMLInputElement]".
  function getFormKey(form) {
    return form.getAttribute("id") || "";
  }

  function getRootUrl() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }

    return "/";
  }

  // Focal redirects to /cart instead of dispatching variant:added when the cart is a page, so a
  // ticked box would never reach the cart. The Liquid guard already skips rendering; this covers
  // cached markup and anything a re-render brings back.
  function redirectsToCartPage() {
    var settings = window.themeVariables && window.themeVariables.settings;

    if (!settings) {
      return false;
    }

    return settings.cartType === "page" || settings.pageType === "cart";
  }

  function hideSlot(slot) {
    if (!slot) {
      return;
    }

    var input = slot.querySelector(INPUT_SELECTOR);

    if (input) {
      input.checked = false;
    }

    slot.hidden = true;
  }

  function hideAllSlots() {
    var slots = document.querySelectorAll(SLOT_SELECTOR);

    for (var i = 0; i < slots.length; i++) {
      hideSlot(slots[i]);
    }
  }

  function getSlotForForm(form) {
    var slot = form.querySelector(SLOT_SELECTOR);

    if (slot) {
      return slot;
    }

    // Fallback for a slot rendered outside the form element.
    var key = getFormKey(form);

    if (!key) {
      return null;
    }

    return document.querySelector(SLOT_SELECTOR + '[data-sc-form-id="' + escapeAttributeValue(key) + '"]');
  }

  function getFormForInput(input) {
    var form = input.closest("form");

    if (form) {
      return form;
    }

    var slot = input.closest(SLOT_SELECTOR);
    var key = slot ? slot.getAttribute("data-sc-form-id") : "";

    return key ? document.getElementById(key) : null;
  }

  function getMainVariantIdFromForm(form) {
    if (!form) {
      return null;
    }

    var variantInput = form.querySelector('[name="id"]');

    return variantInput ? Number(variantInput.value) : null;
  }

  function isBusy(form, slot) {
    var key = getFormKey(form);

    if (key) {
      return inFlight[key] === true;
    }

    return slot.hasAttribute("data-sc-crosssell-busy");
  }

  function setBusy(form, slot, busy) {
    var key = getFormKey(form);

    if (key) {
      if (busy) {
        inFlight[key] = true;
      } else {
        delete inFlight[key];
      }

      return;
    }

    // Attribute fallback for the rare form without an id.
    if (busy) {
      slot.setAttribute("data-sc-crosssell-busy", "");
    } else {
      slot.removeAttribute("data-sc-crosssell-busy");
    }
  }

  // Publish to the Shopify analytics bus rather than writing into a dataLayer directly, so tag
  // management stays in one place.
  function publish(eventName, payload) {
    if (!window.Shopify || !window.Shopify.analytics || typeof window.Shopify.analytics.publish !== "function") {
      return;
    }

    window.Shopify.analytics.publish(eventName, payload);
  }

  // Ready-formatted strings emitted by the snippet, one entry per selectable variant. Read fresh
  // on every event: the theme replaces parts of the product page on variant changes.
  function readPrices(slot) {
    var node = slot.querySelector(PRICES_SELECTOR);

    if (!node) {
      return null;
    }

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  // The picker wins when present; without one the offer is fixed and the checkbox carries it.
  function getSelectedAddon(slot) {
    var select = slot.querySelector(SELECT_SELECTOR);

    if (select) {
      var prices = readPrices(slot);
      var entry = prices ? prices[select.value] : null;

      return {
        id: Number(select.value),
        cents: entry ? Number(entry.cents) : 0
      };
    }

    var input = slot.querySelector(INPUT_SELECTOR);

    if (!input) {
      return null;
    }

    return {
      id: Number(input.getAttribute("data-sc-addon-variant-id")),
      cents: Number(input.getAttribute("data-sc-addon-price"))
    };
  }

  // Swap the strings, never compute a price. Only the two money-derived values go through
  // innerHTML, because shop.money_format may legitimately carry markup; the reference unit comes
  // from a merchant-editable metafield and is set as text.
  function applyPrices(slot) {
    var select = slot.querySelector(SELECT_SELECTOR);

    if (!select) {
      return;
    }

    var prices = readPrices(slot);
    var entry = prices ? prices[select.value] : null;

    if (!entry) {
      return;
    }

    var priceNode = slot.querySelector("[data-sc-crosssell-price]");

    if (priceNode) {
      priceNode.innerHTML = entry.price;
    }

    var unitNode = slot.querySelector("[data-sc-crosssell-unit-price]");

    if (!unitNode) {
      return;
    }

    if (!entry.unit) {
      unitNode.hidden = true;
      return;
    }

    setText(unitNode, "[data-sc-crosssell-unit-ref-value]", entry.unit.refValue);
    setText(unitNode, "[data-sc-crosssell-unit-ref-unit]", entry.unit.refUnit);

    var unitValueNode = unitNode.querySelector("[data-sc-crosssell-unit-value]");

    if (unitValueNode) {
      unitValueNode.innerHTML = entry.unit.value;
    }

    unitNode.hidden = false;
  }

  function setText(root, selector, value) {
    var node = root.querySelector(selector);

    if (node) {
      node.textContent = value == null ? "" : String(value);
    }
  }

  function buildPayload(slot, mainVariantId) {
    var addon = getSelectedAddon(slot);

    if (!addon) {
      return null;
    }

    return {
      addon_variant_id: addon.id,
      main_variant_id: mainVariantId,
      value: addon.cents / 100
    };
  }

  function addAddonToCart(form, slot, mainVariantId) {
    // Read the picker at submit time, not when the box was ticked: the customer may have changed
    // the variant in between.
    var addon = getSelectedAddon(slot);

    if (!addon || !addon.id) {
      return;
    }

    var addonVariantId = addon.id;
    var rootUrl = getRootUrl();

    setBusy(form, slot, true);

    fetch(rootUrl + "cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest"
      },
      body: JSON.stringify({
        items: [
          {
            id: addonVariantId,
            quantity: 1,
            // Underscore-prefixed properties stay hidden from the customer while keeping the
            // link between add-on and main line for fulfilment and reporting.
            properties: { _sc_addon_of: String(mainVariantId) }
          }
        ],
        sections: "mini-cart",
        sections_url: window.location.pathname
      })
    })
      .then(function (response) {
        if (!response.ok) {
          throw new Error("cart/add.js responded with " + response.status);
        }

        return response.json();
      })
      .then(function (addResponse) {
        return fetch(rootUrl + "cart.js")
          .then(function (response) {
            return response.json();
          })
          .then(function (cartContent) {
            // Same sequence as ProductForm in theme.js: cart:updated first, then patch the
            // rendered sections from the add response onto the cart object before cart:refresh,
            // because /cart.js itself never returns sections.
            document.documentElement.dispatchEvent(
              new CustomEvent("cart:updated", {
                bubbles: true,
                detail: { cart: cartContent }
              })
            );

            cartContent["sections"] = addResponse["sections"];

            document.documentElement.dispatchEvent(
              new CustomEvent("cart:refresh", {
                bubbles: true,
                detail: {
                  cart: cartContent,
                  // The theme's own dispatch already opened the drawer; we only need the redraw.
                  openMiniCart: false,
                  replacementDelay: REPLACEMENT_DELAY
                }
              })
            );

            setBusy(form, slot, false);

            // Read the checkbox again: the theme may have replaced the node in the meantime.
            var currentSlot = getSlotForForm(form);
            var currentInput = currentSlot ? currentSlot.querySelector(INPUT_SELECTOR) : null;

            if (currentInput) {
              currentInput.checked = false;
            }

            publish("sc_crosssell_added", {
              addon_variant_id: addon.id,
              main_variant_id: mainVariantId,
              value: addon.cents / 100
            });
          });
      })
      .catch(function (error) {
        setBusy(form, slot, false);

        // The main product is already safely in the cart, so there is nothing to recover and
        // nothing worth interrupting the customer for. Log once and retire the offer.
        console.warn("[sc-crosssell] add-on could not be added to the cart", error);
        hideSlot(getSlotForForm(form));
      });
  }

  if (redirectsToCartPage()) {
    hideAllSlots();
  }

  // One listener, two branches. Resolving the slot first and dispatching on the origin keeps the
  // picker from being swallowed by an early return meant for the checkbox.
  document.addEventListener("change", function (event) {
    if (!(event.target instanceof Element)) {
      return;
    }

    var slot = event.target.closest(SLOT_SELECTOR);

    if (!slot) {
      return;
    }

    if (redirectsToCartPage()) {
      hideSlot(slot);
      return;
    }

    if (event.target.closest(SELECT_SELECTOR)) {
      // Prices only. The checkbox is deliberately left alone: a paid add-on may only enter the
      // cart through an explicit tick (section 312a (3) BGB), never as a side effect of picking
      // a variant.
      applyPrices(slot);
      return;
    }

    var input = event.target.closest(INPUT_SELECTOR);

    // Only the opt-in is worth a signal; unticking is not an intent we act on.
    if (!input || !input.checked) {
      return;
    }

    var payload = buildPayload(slot, getMainVariantIdFromForm(getFormForInput(input)));

    if (payload) {
      publish("sc_crosssell_toggled", payload);
    }
  });

  document.addEventListener("variant:added", function (event) {
    var form = event.target;

    if (!form || form.tagName !== "FORM" || redirectsToCartPage()) {
      return;
    }

    var slot = getSlotForForm(form);

    if (!slot || slot.hidden) {
      return;
    }

    var input = slot.querySelector(INPUT_SELECTOR);

    if (!input || !input.checked) {
      return;
    }

    // A fast double click fires variant:added twice, and the checkbox is only cleared once our
    // add succeeds - without this the add-on would land in the cart twice.
    if (isBusy(form, slot)) {
      return;
    }

    // event.detail.variant is the line item returned by cart/add.js, so .id is the variant id.
    var mainVariantId = event.detail && event.detail.variant ? event.detail.variant.id : null;

    if (!mainVariantId) {
      return;
    }

    addAddonToCart(form, slot, mainVariantId);
  });
})();
